import { useState } from "react";
import { Drawer } from "antd";

import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { useListRuleVersions } from "../../../../../queries/withdrawal-rules-v2/useListRuleVersions";
import {
  graphToToggleState,
  type DeserializeReason,
} from "../../../../../queries/withdrawal-rules-v2/mappers";
import {
  summarizeToggleState,
  type RuleSummary,
} from "../../../../../queries/withdrawal-rules-v2/summarizeToggleState";
import type {
  RuleGraph,
  RuleVersion,
} from "../../../../../queries/withdrawal-rules-v2/types";
import styles from "./VersionsDrawer.module.css";

// Maps the mapper's `DeserializeReason` codes to admin-facing labels.
// Exhaustive by construction — a new reason code added in mappers.ts
// forces TypeScript to require a new entry here, which is the whole
// point: we never want to render a bare reason code in the UI.
const REASON_LABEL: Readonly<Record<DeserializeReason, string>> = {
  unrecognized_structure:
    AppStrings.RULES_VERSIONS_DETAILS_REASON_UNRECOGNIZED_STRUCTURE,
  unknown_feature:
    AppStrings.RULES_VERSIONS_DETAILS_REASON_UNKNOWN_FEATURE,
  unexpected_operator:
    AppStrings.RULES_VERSIONS_DETAILS_REASON_UNEXPECTED_OPERATOR,
  unexpected_literal:
    AppStrings.RULES_VERSIONS_DETAILS_REASON_UNEXPECTED_LITERAL,
};

export interface VersionsDrawerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

/**
 * The BE version-id format is `<YYYYMMDDTHHMMSSZ>-<hex>` (see spec).
 * Parse strictly so unrecognised ids fall through to a raw-id
 * display instead of being split at an arbitrary dash. `null`
 * signals "not our format" and the caller should display the raw
 * string.
 */
function parseVersionId(
  id: string,
): { readonly human: string; readonly hash: string } | null {
  const m = id.match(
    /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z-([a-f0-9]+)$/i,
  );
  if (!m) return null;
  const [, y, mo, d, h, mi, s, hash] = m;
  return {
    human: `${y}-${mo}-${d} ${h}:${mi}:${s} UTC`,
    hash,
  };
}

export default function VersionsDrawer({ isOpen, onClose }: VersionsDrawerProps) {
  const versionsQuery = useListRuleVersions();

  // Which row's dropdown is currently expanded. Only one at a time
  // — collapsing the previous when a new one opens keeps the
  // drawer compact and mirrors typical accordion UX for
  // audit-style panels.
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);

  const toggleExpanded = (versionId: string) => {
    setExpandedVersion((prev) => (prev === versionId ? null : versionId));
  };

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={AppStrings.RULES_VERSIONS_DRAWER_TITLE}
      placement="right"
      width={480}
      destroyOnClose
      data-testid="rules-versions-drawer"
    >
      <p className={styles.subtitle}>
        {AppStrings.RULES_VERSIONS_DRAWER_SUBTITLE}
      </p>
      <VersionsBody
        query={versionsQuery}
        expandedVersion={expandedVersion}
        onToggleExpanded={toggleExpanded}
      />
    </Drawer>
  );
}

type VersionsBodyProps = {
  readonly query: ReturnType<typeof useListRuleVersions>;
  readonly expandedVersion: string | null;
  readonly onToggleExpanded: (versionId: string) => void;
};

function VersionsBody({
  query,
  expandedVersion,
  onToggleExpanded,
}: VersionsBodyProps) {
  if (query.isPending) {
    return (
      <p className={styles.stateMessage} data-testid="rules-versions-loading">
        {AppStrings.RULES_VERSIONS_LOADING}
      </p>
    );
  }

  if (query.isError) {
    return (
      <p
        className={styles.stateMessage}
        data-testid="rules-versions-error"
        role="alert"
      >
        {AppStrings.RULES_VERSIONS_ERROR}
      </p>
    );
  }

  const versions: readonly RuleVersion[] = query.data ?? [];
  if (versions.length === 0) {
    return (
      <p className={styles.stateMessage} data-testid="rules-versions-empty">
        {AppStrings.RULES_VERSIONS_EMPTY}
      </p>
    );
  }

  return (
    <ul className={styles.list} data-testid="rules-versions-list">
      {versions.map((v) => {
        const parsed = parseVersionId(v.version);
        const isExpanded = expandedVersion === v.version;
        const detailsPanelId = `rules-versions-details-${v.version}`;
        return (
          <li
            key={v.version}
            className={styles.row}
            data-testid={`rules-versions-row-${v.version}`}
          >
            <div className={styles.rowHeader}>
              <div className={styles.rowInfo}>
                <div className={styles.rowStampLine}>
                  <span className={styles.rowStamp}>
                    {parsed?.human ?? v.version}
                  </span>
                  {v.is_current && (
                    <span
                      className={styles.currentBadge}
                      data-testid={`rules-versions-current-badge-${v.version}`}
                    >
                      {AppStrings.RULES_VERSIONS_CURRENT_BADGE}
                    </span>
                  )}
                </div>
                {parsed && (
                  <code className={styles.rowHash}>{parsed.hash}</code>
                )}
              </div>
              <button
                type="button"
                className={styles.expandButton}
                onClick={() => onToggleExpanded(v.version)}
                aria-expanded={isExpanded}
                aria-controls={detailsPanelId}
                data-testid={`rules-versions-expand-${v.version}`}
              >
                <span className={styles.expandButtonLabel}>
                  {isExpanded
                    ? AppStrings.RULES_VERSIONS_COLLAPSE_BUTTON
                    : AppStrings.RULES_VERSIONS_EXPAND_BUTTON}
                </span>
                <span
                  aria-hidden="true"
                  className={
                    isExpanded
                      ? `${styles.expandChevron} ${styles.expandChevronOpen}`
                      : styles.expandChevron
                  }
                >
                  ▾
                </span>
              </button>
            </div>
            {isExpanded && (
              <VersionDetails
                id={detailsPanelId}
                versionId={v.version}
                graph={v.graph}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

// The graph is delivered inline with every row of
// `GET /rules/versions`, so there is no per-row loading or error
// state to render here — those live at the drawer level, where the
// whole list is either resolved, pending, or errored. If the mapper
// can't decode the graph into the toggle vocabulary, we fall through
// to the "advanced graph" placeholder (same fallback the editor's
// read-only banner uses).
type VersionDetailsProps = {
  readonly id: string;
  readonly versionId: string;
  readonly graph: RuleGraph;
};

function VersionDetails({ id, versionId, graph }: VersionDetailsProps) {
  const decoded = graphToToggleState(graph);
  if (!decoded.ok) {
    return (
      <div
        id={id}
        className={styles.details}
        data-testid={`rules-versions-details-advanced-${versionId}`}
      >
        <p className={styles.detailsStateMessage}>
          {AppStrings.RULES_VERSIONS_DETAILS_ADVANCED}
        </p>
        <div
          className={styles.detailsAdvancedDiagnostic}
          data-testid={`rules-versions-details-advanced-diagnostic-${versionId}`}
        >
          <p
            className={styles.detailsAdvancedReason}
            data-testid={`rules-versions-details-advanced-reason-${versionId}`}
          >
            <span className={styles.detailsAdvancedReasonLabel}>
              {AppStrings.RULES_VERSIONS_DETAILS_REASON_LABEL}
            </span>
            {REASON_LABEL[decoded.reason]}
          </p>
          <code
            className={styles.detailsAdvancedDetail}
            data-testid={`rules-versions-details-advanced-detail-${versionId}`}
          >
            {decoded.detail}
          </code>
        </div>
      </div>
    );
  }

  const summary = summarizeToggleState(decoded.state);

  return (
    <div
      id={id}
      className={styles.details}
      data-testid={`rules-versions-details-${versionId}`}
    >
      <TierSection
        heading={AppStrings.RULES_VERSIONS_DETAILS_TIER1_HEADING}
        rules={summary.tier1}
        emptyLabel={AppStrings.RULES_VERSIONS_DETAILS_TIER_EMPTY}
        testId={`rules-versions-details-${versionId}-tier1`}
      />
      <TierSection
        heading={AppStrings.RULES_VERSIONS_DETAILS_TIER2_HEADING}
        rules={summary.tier2}
        emptyLabel={AppStrings.RULES_VERSIONS_DETAILS_TIER_EMPTY}
        testId={`rules-versions-details-${versionId}-tier2`}
      />
    </div>
  );
}

type TierSectionProps = {
  readonly heading: string;
  readonly rules: readonly RuleSummary[];
  readonly emptyLabel: string;
  readonly testId: string;
};

function TierSection({ heading, rules, emptyLabel, testId }: TierSectionProps) {
  return (
    <section className={styles.tierSection} data-testid={testId}>
      <h4 className={styles.tierHeading}>{heading}</h4>
      {rules.length === 0 ? (
        <p className={styles.tierEmpty}>{emptyLabel}</p>
      ) : (
        <ul className={styles.ruleList}>
          {rules.map((rule) => (
            <li
              key={rule.id}
              className={styles.ruleRow}
              data-testid={`${testId}-rule-${rule.id}`}
            >
              <span className={styles.ruleLabel}>{rule.label}</span>
              <span className={styles.ruleDetail}>{rule.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
